import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { usePoll } from "../hooks/usePoll";

/**
 * Merchant chat — the in-app side of the Roam-bridged communication
 * channels (/api/merchant-chat/*).
 *
 * Channel list and nav badge are plain polled queries. The open thread is
 * a merged local store: an initial latest-window fetch, a 5s tail poll
 * (re-fetching the last TAIL_OVERLAP ids so Roam-side edits and deletes
 * within the window are picked up, not just new messages), a "load older"
 * pager, and optimistic sends keyed by client_key.
 */

const TAIL_OVERLAP = 50;
const POLL_MS = 5000;

export const useChatChannels = (enabled = true) =>
  useQuery({
    queryKey: ["merchant-chat", "channels"],
    enabled,
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data } = await axios.get("/api/merchant-chat/channels", {
        metadata: { silent: true },
      });
      return data?.channels ?? [];
    },
  });

export const useChatSummary = (enabled = true) =>
  useQuery({
    queryKey: ["merchant-chat", "summary"],
    enabled,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data } = await axios.get("/api/merchant-chat/summary", {
        metadata: { silent: true },
      });
      return data ?? { unread_count: 0 };
    },
  });

export const useRefreshChat = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["merchant-chat"] });
};

/** Merge server messages into the ordered map — replaces edits in place. */
const mergeMessages = (current, incoming) => {
  if (!incoming?.length) return current;
  const byId = new Map(current.map((m) => [m.id, m]));
  const byClientKey = new Map(
    current.filter((m) => m.client_key).map((m) => [m.client_key, m])
  );
  let changed = false;

  incoming.forEach((message) => {
    const existing =
      byId.get(message.id) ||
      (message.client_key ? byClientKey.get(message.client_key) : null);

    if (existing) {
      // Replace (covers optimistic → server swap and Roam-side edits).
      if (JSON.stringify(existing) !== JSON.stringify(message)) {
        byId.delete(existing.id);
        byId.set(message.id, message);
        changed = true;
      }
    } else {
      byId.set(message.id, message);
      changed = true;
    }
  });

  if (!changed) return current;
  return [...byId.values()].sort((a, b) => {
    // Optimistic messages carry a temporary string id — keep them last.
    const aTemp = typeof a.id === "string";
    const bTemp = typeof b.id === "string";
    if (aTemp !== bTemp) return aTemp ? 1 : -1;
    return aTemp ? 0 : a.id - b.id;
  });
};

export const useChatThread = (channelId) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const refreshChat = useRefreshChat();
  // The poll callback reads the latest list without re-arming the interval.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const lastRealId = useCallback(() => {
    const real = messagesRef.current.filter((m) => typeof m.id === "number");
    return real.length ? real[real.length - 1].id : 0;
  }, []);

  // Initial window (and reset) per channel.
  useEffect(() => {
    if (!channelId) return undefined;
    let cancelled = false;
    setMessages([]);
    setIsLoading(true);
    setIsError(false);

    axios
      .get(`/api/merchant-chat/channels/${channelId}/messages`, {
        metadata: { silent: true },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setMessages((current) => mergeMessages(current, data?.messages ?? []));
        setHasOlder(Boolean(data?.has_more));
      })
      .catch(() => !cancelled && setIsError(true))
      .finally(() => !cancelled && setIsLoading(false));

    return () => {
      cancelled = true;
    };
  }, [channelId]);

  // Tail poll — overlaps the last TAIL_OVERLAP ids to catch edits/deletes.
  // usePoll (M4.1): a failed tail backs off instead of re-asking every 5 s,
  // stops on 401/403/404, and a hidden tab asks nothing. Still silent.
  const tailPoll = useCallback(async () => {
    const after = Math.max(0, lastRealId() - TAIL_OVERLAP);
    const { data } = await axios.get(`/api/merchant-chat/channels/${channelId}/messages`, {
      params: { after },
      metadata: { silent: true },
    });
    setMessages((current) => mergeMessages(current, data?.messages ?? []));
  }, [channelId, lastRealId]);

  usePoll(tailPoll, { interval: POLL_MS, enabled: !!channelId });

  const loadOlder = useCallback(() => {
    const real = messagesRef.current.filter((m) => typeof m.id === "number");
    if (!real.length || !channelId) return Promise.resolve();

    return axios
      .get(`/api/merchant-chat/channels/${channelId}/messages`, {
        params: { before: real[0].id },
        metadata: { silent: true },
      })
      .then(({ data }) => {
        setMessages((current) => mergeMessages(current, data?.messages ?? []));
        setHasOlder(Boolean(data?.has_more));
      })
      .catch(() => {});
  }, [channelId]);

  /** Optimistic send — text and/or File objects. Returns a promise. */
  const send = useCallback(
    (body, files = [], existingClientKey = null) => {
      if (!channelId) return Promise.resolve();

      const clientKey =
        existingClientKey ||
        `ck-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      const optimistic = {
        id: `tmp-${clientKey}`,
        channel_id: channelId,
        direction: "out",
        content_type: files.length ? "files" : "text",
        body: body || null,
        status: "pending",
        client_key: clientKey,
        author: { name: "You", is_self: true, is_rc: false },
        attachments: files.map((file, index) => ({
          index,
          name: file.name,
          mime: file.type,
          size: file.size,
          type: file.type?.startsWith("image/") ? "photo" : "blob",
          url: URL.createObjectURL(file),
          thumbnail_url: null,
        })),
        created_at: new Date().toISOString(),
      };

      setMessages((current) =>
        mergeMessages(
          current.filter((m) => m.client_key !== clientKey),
          [optimistic]
        )
      );

      const form = new FormData();
      if (body) form.append("body", body);
      form.append("client_key", clientKey);
      files.forEach((file) => form.append("files[]", file));

      return axios
        .post(`/api/merchant-chat/channels/${channelId}/messages`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          metadata: { silent: true },
        })
        .then(({ data }) => {
          if (data?.message) {
            setMessages((current) =>
              mergeMessages(current, [data.message])
            );
          }
          refreshChat();
        })
        .catch(() => {
          setMessages((current) =>
            current.map((m) =>
              m.client_key === clientKey ? { ...m, status: "failed", _files: files } : m
            )
          );
        });
    },
    [channelId, refreshChat]
  );

  const retry = useCallback(
    (message) => {
      setMessages((current) => current.filter((m) => m.id !== message.id));
      return send(message.body, message._files || [], message.client_key);
    },
    [send]
  );

  return { messages, isLoading, isError, hasOlder, loadOlder, send, retry };
};

export const markChannelRead = (channelId, lastReadMessageId) =>
  axios
    .put(
      `/api/merchant-chat/channels/${channelId}/read`,
      { last_read_message_id: lastReadMessageId },
      { metadata: { silent: true } }
    )
    .catch(() => {});

/**
 * Mint a Roam meeting link and post it into the channel (RC master admins).
 *
 * Errors are rethrown on purpose, unlike markChannelRead: a 422 carries
 * Roam's own reason — a credential without `meetinglink:write`, a host who
 * is not a workspace member, a channel with no group — and the dialog shows
 * that text verbatim. Swallowing it would leave the user pressing a button
 * that silently does nothing.
 *
 * The posted link arrives in the thread through the normal 5s tail poll, so
 * there is no optimistic row to reconcile here.
 */
export const createChannelMeeting = (channelId, { name, start = null, end = null, timezone = null }) =>
  axios
    // `end` is NOT optional decoration. The backend only takes the calendar
    // path — real event, real invitations — when start AND end are present,
    // and Roam rejects a meeting link that carries a start without an end.
    // This signature lost `end` when the duration control was removed and
    // did not get it back when the control returned, so every scheduled
    // meeting failed with `missing_parameter` (prod, 2026-10-01).
    .post(`/api/merchant-chat/channels/${channelId}/meeting`, { name, start, end, timezone })
    .then(({ data }) => data.message);
